/**
 * Haptic feedback utility using Capacitor Haptics API
 * Provides native haptic feedback on iOS and Android
 * Falls back to Web Vibration API on web, silently fails if unavailable
 */

import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";

/**
 * Check if we're running in a native app context
 */
const isNative = Capacitor.isNativePlatform();

/**
 * Trigger a light tap feedback - for selections and toggles
 */
export async function hapticLight(): Promise<void> {
  try {
    if (isNative) {
      await Haptics.impact({ style: ImpactStyle.Light });
    } else if ("vibrate" in navigator) {
      navigator.vibrate(10);
    }
  } catch {
    // Silently fail - haptics are nice-to-have
  }
}

/**
 * Trigger a medium impact feedback - for button presses
 */
export async function hapticMedium(): Promise<void> {
  try {
    if (isNative) {
      await Haptics.impact({ style: ImpactStyle.Medium });
    } else if ("vibrate" in navigator) {
      navigator.vibrate(25);
    }
  } catch {
    // Silently fail
  }
}

/**
 * Trigger a heavy impact feedback - for significant actions
 */
export async function hapticHeavy(): Promise<void> {
  try {
    if (isNative) {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } else if ("vibrate" in navigator) {
      navigator.vibrate(50);
    }
  } catch {
    // Silently fail
  }
}

/**
 * Trigger a success feedback - for completed actions
 */
export async function hapticSuccess(): Promise<void> {
  try {
    if (isNative) {
      await Haptics.notification({ type: NotificationType.Success });
    } else if ("vibrate" in navigator) {
      navigator.vibrate([10, 50, 30]);
    }
  } catch {
    // Silently fail
  }
}

/**
 * Trigger a warning feedback
 */
export async function hapticWarning(): Promise<void> {
  try {
    if (isNative) {
      await Haptics.notification({ type: NotificationType.Warning });
    } else if ("vibrate" in navigator) {
      navigator.vibrate([30, 50, 30, 50, 30]);
    }
  } catch {
    // Silently fail
  }
}

/**
 * Trigger an error feedback
 */
export async function hapticError(): Promise<void> {
  try {
    if (isNative) {
      await Haptics.notification({ type: NotificationType.Error });
    } else if ("vibrate" in navigator) {
      navigator.vibrate([50, 100, 50]);
    }
  } catch {
    // Silently fail
  }
}

/**
 * Trigger a selection feedback - for list selections, tab switches
 */
export async function hapticSelection(): Promise<void> {
  try {
    if (isNative) {
      await Haptics.selectionChanged();
    } else if ("vibrate" in navigator) {
      navigator.vibrate(5);
    }
  } catch {
    // Silently fail
  }
}

/**
 * Trigger a simple vibration with custom duration
 * @param duration - Duration in milliseconds (default 300)
 */
export async function hapticVibrate(duration: number = 300): Promise<void> {
  try {
    if (isNative) {
      await Haptics.vibrate({ duration });
    } else if ("vibrate" in navigator) {
      navigator.vibrate(duration);
    }
  } catch {
    // Silently fail
  }
}
